import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  API,
  authHeaders,
  alertError,
  alertSuccess,
  btnPrimary,
  fieldStyle,
  labelStyle,
  pageTitleStyle,
  pageSubtitleStyle,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import SearchableSelect, { ticketsToOptions } from '../../../components/SearchableSelect';
import { ensureTicketInList, usePrefillTicketNo } from '../hooks/usePrefillTicketNo';
import { TicketActionBack, TicketDetailLink } from '../ticketNav';
import { useProductFilter } from '../ProductFilterContext';

function TicketManagerQueuePage() {
  const prefillTicket = usePrefillTicketNo();
  const { productId, selectedProduct } = useProductFilter();
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState(prefillTicket);
  const [assignees, setAssignees] = useState([]);
  const [picked, setPicked] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const filteredQueue = useMemo(
    () => (productId ? queue.filter((t) => t.product_id === productId) : queue),
    [queue, productId],
  );

  const ticketOptions = useMemo(
    () => ticketsToOptions(filteredQueue, (t) => (
      t.assignment_due_at ? `assign by ${formatUtcToIst(t.assignment_due_at)}` : ''
    )),
    [filteredQueue],
  );

  const loadQueue = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/assignments/manager-queue`, authHeaders());
      const list = Array.isArray(res.data) ? res.data : [];
      setQueue(list);
      if (prefillTicket) {
        setSelected(prefillTicket);
        if (!list.some((t) => t.ticket_no === prefillTicket)) {
          await ensureTicketInList(prefillTicket, setQueue);
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadQueue(); }, [prefillTicket, productId]);

  useEffect(() => {
    if (!selected) { setAssignees([]); setPicked(''); return; }
    axios.get(`${API}/assignments/downstream-assignees?ticket_no=${selected}`, authHeaders())
      .then((r) => setAssignees(r.data))
      .catch(() => setAssignees([]));
  }, [selected]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected || !picked) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/assignments/`, {
        ticket_no: selected, assignee_ids: [picked], notes: notes || null,
      }, authHeaders());
      setSuccess(`Ticket ${selected} assigned`);
      setSelected('');
      setPicked('');
      setNotes('');
      loadQueue();
    } catch (err) {
      setError(err.response?.data?.detail || 'Assign failed');
    } finally {
      setSubmitting(false);
    }
  };

  const ticket = queue.find((t) => t.ticket_no === selected);

  if (loading) return <p style={{ color: '#74777F' }}>Loading manager queue…</p>;

  return (
    <div style={{ maxWidth: '960px' }}>
      <div className="tk-page-head">
        <div className="tk-page-head-main">
          <div className="tk-page-head-back">
            <TicketActionBack ticketNo={selected || prefillTicket || null} />
          </div>
          <h2 style={pageTitleStyle}>Manager Queue</h2>
          <p style={pageSubtitleStyle}>
            Assign new tickets to leads or members — or take them yourself.
          </p>
        </div>
      </div>
      {error && <div style={alertError}>{String(error)}</div>}
      {success && <div style={alertSuccess}>{success}</div>}

      <form onSubmit={submit} style={{ display: 'grid', gap: '14px' }}>
        <div>
          <label className="tk-label" style={labelStyle}>Your queue ({queue.length})</label>
          <SearchableSelect
            options={ticketOptions}
            value={selected}
            onChange={setSelected}
            variant="ticket"
            placeholder="Search and select a ticket…"
            searchPlaceholder="Search by ticket no, subject, domain…"
            aria-label="Manager queue ticket"
          />
        </div>

        {ticket && (
          <div className="tk-field-readonly" style={{ padding: '12px' }}>
            <TicketDetailLink ticketNo={ticket.ticket_no} className="tk-link" style={{ fontWeight: 600 }}>{ticket.ticket_no}</TicketDetailLink>
            <p style={{ margin: '8px 0 0', fontSize: '13px' }}>{ticket.description}</p>
          </div>
        )}

        {selected && (
          <div>
            <label className="tk-label" style={labelStyle}>Assign to (select one)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {assignees.map((a) => (
                <label
                  key={a.user_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    border: picked === a.user_id ? '2px solid #002FA7' : '1px solid #C9CED8',
                    borderRadius: '999px',
                    background: picked === a.user_id ? '#EEF0FF' : '#fff',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="manager-queue-assignee"
                    checked={picked === a.user_id}
                    onChange={() => setPicked(a.user_id)}
                  />
                  {a.name} ({a.workload ?? 0} open)
                </label>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="tk-label" style={labelStyle}>Notes</label>
          <textarea className="tk-field" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={fieldStyle} />
        </div>

        <button type="submit" disabled={submitting || !selected || !picked} className="tk-btn tk-btn-primary" style={{ ...btnPrimary, width: 'fit-content' }}>
          Assign downstream
        </button>
      </form>
    </div>
  );
}

export default TicketManagerQueuePage;
